import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'

import { User } from '../types'
import { storageKey, productionHades, credentialEvent } from '../config'
import { getItem } from '../hooks/useStickyState'
import { emptyCredential } from '../helpers/credentials'

import {
  getAccessTokenFromHeaders,
  getRefreshTokenFromHeaders,
} from '../helpers/headers'

window.hades = window.hades || {
  serverUrl: productionHades,
}

const http = axios.create({
  baseURL: window.hades.serverUrl,
  withCredentials: true,
})

export type CredentialResponse = {
  user: User
  token: string
  refreshToken?: string
}

/* Redirects to the Google OAuth flow.
 *
 * @param redirectUri - The URI to redirect after login.
 */
export const loginWithGoogle = (
  redirectUri: string | undefined = window.location.origin
): void => {
  const encodedUri = decodeURIComponent(redirectUri)

  window.location.href = `${window.hades.serverUrl}/api/authenticate?redirect_uri=${encodedUri}`
}

/* Log in with e-mail and password
 */
export const loginWithEmailAndPassword = async (credentials: {
  username: string
  password: string
}): Promise<CredentialResponse> => {
  const { data, headers } = await http.post('/api/authenticate', credentials)

  return {
    token: getAccessTokenFromHeaders(headers),
    refreshToken: getRefreshTokenFromHeaders(headers),
    user: data,
  }
}

/* Get the user data and token
 *
 * @param sessionId - The sessionId value received after the OAuth flow
 */
export const authenticateComplete = async (
  sessionId: string
): Promise<CredentialResponse> => {
  try {
    const { data: user, headers } = await http.get(
      '/api/authenticate/complete',
      {
        headers: {
          SESSION: sessionId,
        },
      }
    )

    return {
      token: getAccessTokenFromHeaders(headers),
      refreshToken: getRefreshTokenFromHeaders(headers),
      user,
    }
  } catch (e) {
    return Promise.reject(e.response.data)
  }
}

/* Get the user for the current token
 *
 * @param token - A valid JWT token
 */
export const getCurrentUser = async (token: string): Promise<User> => {
  try {
    const { data } = await http.get('/api/users/current', {
      headers: {
        Authorization: token,
      },
    })

    return data
  } catch (e) {
    return Promise.reject(e.response.data)
  }
}

/* Remove the credentials from localStorage.
 */
export const clearAuth = () => {
  localStorage.setItem(storageKey, JSON.stringify(emptyCredential))
}

const getToken = async (refreshToken: string): Promise<CredentialResponse> => {
  const { data: user, headers } = await http.post('/api/authenticate/token', {
    token: refreshToken,
  })

  return {
    token: getAccessTokenFromHeaders(headers),
    refreshToken: getRefreshTokenFromHeaders(headers),
    user,
  }
}

const getRefreshedCredential = ({
  token,
  user,
}: CredentialResponse): CredentialResponse => {
  const currentCredentials = localStorage.getItem(storageKey)

  try {
    const parsedCredentials = JSON.parse(currentCredentials)

    return {
      ...parsedCredentials,
      user,
      token,
    }
  } catch (e) {
    return { user, token }
  }
}

const persistCredential = (
  credential: CredentialResponse
): CredentialResponse => {
  localStorage.setItem(storageKey, JSON.stringify(credential))

  return credential
}

const dispatchCredential = (
  credential: CredentialResponse
): CredentialResponse => {
  // We dispatch a custom event so the React tree can capture the new credential and update the security provider state
  window.dispatchEvent(new CustomEvent(credentialEvent, { detail: credential }))

  return credential
}

const getCurrentCredential = () => {
  const currentCredentials = getItem(storageKey)

  try {
    const parsedCredentials = JSON.parse(currentCredentials)

    return parsedCredentials
  } catch {
    return null
  }
}

export const refreshCredential = (refreshToken: string) =>
  getToken(refreshToken)
    .then(getRefreshedCredential)
    .then(persistCredential)
    .then(dispatchCredential)

/* Intercepts the axios requests and add the token.
 */
const requestInterceptor = async (
  config: AxiosRequestConfig
): Promise<AxiosRequestConfig> => {
  try {
    const auth = getItem(storageKey)

    if (auth) {
      const { token } = JSON.parse(auth)

      if (token) {
        config.headers['authorization'] = token
      }
    }
  } catch (e) {}

  return config
}

/* Intercepts the axios response error.
 */
const responseErrorInterceptor = async (
  error: AxiosError<any>
): Promise<any> => {
  const isHashHistory = window.location.hash !== ''

  const loginPath = isHashHistory && window.hades?.loginPath
    ? `#${window.hades?.loginPath}`
    : window.hades?.loginPath

  // redirect to login if response status is 401
  if (error?.response?.status === 401) {
    const config = error.config

    try {
      const auth = getItem(storageKey)

      if (auth) {
        const { refreshToken } = JSON.parse(auth)

        if (refreshToken) {
          const { token: newToken } = await refreshCredential(refreshToken)

          config.headers['authorization'] = newToken

          return axios(config)
        }
      }
    } catch (e) {}
  }

  if (
    error?.response?.status === 401 &&
    window.location.pathname !== loginPath
  ) {
    window.location.href = `${window.location.origin}${loginPath || '/#/login'}`
  }

  return Promise.reject(error)
}

const hadesRequestInterceptor = (
  config: AxiosRequestConfig
): AxiosRequestConfig => {
  config.baseURL = window.hades.serverUrl

  return config
}

http.interceptors.request.use(hadesRequestInterceptor)

/* Add the requestInterceptor and responseErrorInterceptor to an axios instance
 */
export const addAuthInterceptors = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(requestInterceptor)
  axiosInstance.interceptors.response.use(
    (config) => config,
    responseErrorInterceptor
  )
}

/**
 * Two Factor Authentication
 */
export const twoFactorVerification = async (code: string) => {
  try {
    const credentials = getCurrentCredential()

    const { data, headers } = await http.post('/api/authenticate/verify', { code }, {
      headers: {
        Authorization: credentials?.token,
      }
    })

    return {
      token: getAccessTokenFromHeaders(headers),
      refreshToken: getRefreshTokenFromHeaders(headers),
      user: data,
    }
  } catch (e) {
    return Promise.reject(e.response.data)
  }
}
